import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Building2, Briefcase, Users, Calculator, Shield, Landmark, Scale, Lightbulb, FileLock, AlertCircle, ChevronLeft, ChevronRight } from "lucide-react";
import { pickContent } from "@/assets/generated";
import { Reveal } from "@/components/vision/reveal";
import { EditorialHeading } from "./editorial-heading";

export function AreasSection() {
  const { t } = useTranslation();
  const areas = [
    { icon: Scale, key: "civil", img: pickContent(3) },
    { icon: Briefcase, key: "commercial", img: pickContent(24) },
    { icon: Users, key: "labor", img: pickContent(9) },
    { icon: Calculator, key: "tax", img: pickContent(22) },
    { icon: Shield, key: "privacy", img: pickContent(31) },
    { icon: Landmark, key: "banking", img: pickContent(8) },
    { icon: AlertCircle, key: "criminal", img: pickContent(13) },
    { icon: Lightbulb, key: "ip", img: pickContent(43) },
    { icon: Building2, key: "admin", img: pickContent(38) },
    { icon: FileLock, key: "bankruptcy", img: pickContent(40) },
  ];
  const scroller = useRef<HTMLDivElement>(null);
  const scrollBy = (dir: 1 | -1) => {
    const el = scroller.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.85), behavior: "smooth" });
  };

  return (
    <section className="bg-background py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <EditorialHeading
          index="No. 04"
          eyebrow={t("areas.eyebrow")}
          title={t("areas.title")}
          subtitle={t("areas.hint")}
        />
      </div>

      <div className="relative mt-12">
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-background to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-background to-transparent" />

        <button
          type="button"
          aria-label={t("areas.scrollLeft")}
          onClick={() => scrollBy(-1)}
          className="absolute left-2 top-1/2 z-20 hidden -translate-y-1/2 rounded-full border border-border bg-card/90 p-2 shadow-md backdrop-blur transition hover:bg-card md:inline-flex"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          type="button"
          aria-label={t("areas.scrollRight")}
          onClick={() => scrollBy(1)}
          className="absolute right-2 top-1/2 z-20 hidden -translate-y-1/2 rounded-full border border-border bg-card/90 p-2 shadow-md backdrop-blur transition hover:bg-card md:inline-flex"
        >
          <ChevronRight className="h-4 w-4" />
        </button>

        <div ref={scroller} className="overflow-x-auto overflow-y-hidden scroll-smooth pb-4 hide-scrollbar">
          <div className="flex gap-4 snap-x snap-mandatory px-4 sm:px-6 lg:px-8">
            {areas.map((a) => {
              const name = t(`areas.items.${a.key}.name`);
              const desc = t(`areas.items.${a.key}.desc`);
              return (
                <div
                  key={a.key}
                  className="snap-start shrink-0 w-64 overflow-hidden rounded-xl border border-border bg-card transition-all duration-500 hover:-translate-y-2 hover:scale-[1.02] hover:border-primary/40 hover:shadow-xl"
                >
                  <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                    <img src={a.img} alt={name} className="h-full w-full object-cover" loading="lazy" decoding="async" />
                    <div className="absolute inset-0 bg-gradient-to-t from-card/80 via-card/10 to-transparent" />
                    <div className="absolute left-3 top-3 flex h-9 w-9 items-center justify-center rounded-lg bg-background/80 text-primary backdrop-blur">
                      <a.icon className="h-4 w-4" />
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-display text-lg font-semibold">{name}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
                    <span className="mt-3 inline-block rounded-full bg-success/10 px-2 py-0.5 text-[10px] font-medium text-success">
                      ● {t("areas.active")}
                    </span>
                  </div>
                </div>
              );
            })}
            <div className="shrink-0 w-4 sm:w-6 lg:w-8" aria-hidden="true" />
          </div>
        </div>
      </div>
    </section>
  );
}
